// API client de Marketing Studio (ajustes por org y bandeja de aprobación).
// Backend (mcp_django #571, apps/marketing/routers_studio.py), montado en /api/v1/admin/marketing:
//   GET  /ajustes                              (marketing:read)
//   PUT  /ajustes                              (marketing:settings · solo persona admin; la 1.ª vez activa Studio)
//   GET  /aprobaciones?status=&limit=          (marketing:read)
//   POST /aprobaciones/{id}/aprobar            (marketing:approve · solo persona admin)
//   POST /aprobaciones/{id}/pedir-cambios      (ídem; exige comentario)
//   POST /aprobaciones/{id}/rechazar           (ídem)
// La org sale SIEMPRE del JWT. Aquí no hay datos de demostración: lo que se aprueba es real.

import { ensureFreshToken } from '@/lib/tokenRefresh';

import { MarketingNoDisponible } from './api';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/marketing';

export type Modo = 'required' | 'auto';
export type CanalAviso = 'dashboard' | 'whatsapp' | 'email';

export interface Ajustes {
  enabled: boolean;
  calendar_view_enabled: boolean;
  approval_posts: Modo;
  approval_reels: Modo;
  approval_campaigns: 'required';
  notify_channels: CanalAviso[];
  max_daily_budget_cents: number | null;
  brand: Record<string, unknown>;
  updated_by: string;
  updated_at: string | null;
  version: number;
}

export type AjustesCambio = Partial<Pick<Ajustes,
  'calendar_view_enabled' | 'approval_posts' | 'approval_reels' | 'notify_channels' | 'max_daily_budget_cents'>>;

export type EstadoAprobacion = 'pending' | 'approved' | 'changes_requested' | 'rejected' | 'superseded' | 'expired';

export interface Medio {
  id: number;
  kind: 'image' | 'video' | string;
  url: string;
}

export interface Pieza {
  id: number;
  platform: string;
  post_type: 'feed' | 'story' | 'reel' | string;
  status: string;
  caption: string;
  scheduled_at: string | null;
  media: Medio[];
  business_id: string | null;
}

export interface Aprobacion {
  id: number;
  target_type: 'post' | 'campaign' | string;
  target_id: string;
  status: EstadoAprobacion;
  requested_by: string;
  decided_by: string;
  decided_at: string | null;
  comment: string;
  created_at: string | null;
  /** La pieza cambió después de pedir la aprobación: ya no se puede decidir sobre esta solicitud. */
  stale: boolean;
  post: Pieza | null;
}

export type Decision = 'aprobar' | 'pedir-cambios' | 'rechazar';

async function studioFetch<T>(path: string, init?: RequestInit, { ausente404 = true } = {}): Promise<T> {
  await ensureFreshToken();
  const jwt = localStorage.getItem('auth_token');
  if (!jwt) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  const res = await fetch(`${DJANGO_PROXY}${BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${jwt}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers || {}),
    },
  });
  // En lecturas, 404/405/501 = el servidor aún no tiene Marketing Studio. En decisiones,
  // 404 significa «esa solicitud ya no existe» y se muestra el mensaje del backend.
  if (ausente404 && (res.status === 404 || res.status === 405 || res.status === 501)) throw new MarketingNoDisponible();
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = (err as { detail?: unknown }).detail || `Error ${res.status} del servidor`;
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return res.json() as Promise<T>;
}

export function fetchAjustes(): Promise<Ajustes> {
  return studioFetch<Ajustes>('/ajustes');
}

export function saveAjustes(cambios: AjustesCambio): Promise<Ajustes> {
  return studioFetch<Ajustes>('/ajustes', { method: 'PUT', body: JSON.stringify(cambios) }, { ausente404: false });
}

export function fetchAprobaciones(status?: EstadoAprobacion, limit = 100): Promise<Aprobacion[]> {
  const q = new URLSearchParams({ limit: String(limit) });
  if (status) q.set('status', status);
  return studioFetch<Aprobacion[]>(`/aprobaciones?${q.toString()}`);
}

export function decidir(id: number, decision: Decision, body: { comment?: string; scheduled_at?: string } = {}): Promise<Aprobacion> {
  return studioFetch<Aprobacion>(`/aprobaciones/${id}/${decision}`, { method: 'POST', body: JSON.stringify(body) }, { ausente404: false });
}
